import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useRef, useCallback } from 'react';

// Auto-sync WHOOP data when user opens app or returns from background
// Uses aggressive caching to minimise API calls and preserve data across sessions
export function useWhoopAutoSync() {
  const queryClient = useQueryClient();
  const lastSyncAttemptRef = useRef<number>(0);
  const SYNC_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes between sync attempts

  // Check if user has active WHOOP connection - cached for 5 minutes
  const { data: connection } = useQuery({
    queryKey: ['whoop-connection-status'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('whoop_connections')
        .select('id, is_active, last_sync_at, token_expires_at')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        return null;
      }
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Sync mutation - only called when truly needed
  const syncMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-whoop-data');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      lastSyncAttemptRef.current = Date.now();
      queryClient.invalidateQueries({ queryKey: ['whoop-data-cached'] });
      queryClient.invalidateQueries({ queryKey: ['whoop-connection-status'] });
    },
    onError: () => {
      lastSyncAttemptRef.current = Date.now();
    },
  });

  // Determine if sync is needed based on multiple factors
  const shouldSync = useCallback(() => {
    if (!connection?.is_active) return false;
    if (syncMutation.isPending) return false;
    
    const timeSinceLastAttempt = Date.now() - lastSyncAttemptRef.current;
    if (timeSinceLastAttempt < SYNC_COOLDOWN_MS) return false;
    
    if (connection.token_expires_at) {
      const expiresAt = new Date(connection.token_expires_at);
      if (expiresAt < new Date()) {
        return false;
      }
    }
    
    if (connection.last_sync_at) {
      const lastSync = new Date(connection.last_sync_at);
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
      if (lastSync > twoHoursAgo) {
        return false;
      }
    }
    
    return true;
  }, [connection?.is_active, connection?.last_sync_at, connection?.token_expires_at, syncMutation.isPending]);

  // Auto-sync on mount if data is stale
  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldSync()) {
        syncMutation.mutate();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [connection?.is_active]);

  // Listen for visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldSync()) {
        syncMutation.mutate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [shouldSync]);

  // Manual trigger function
  const triggerSync = useCallback(() => {
    const MANUAL_COOLDOWN_MS = 60 * 1000;
    const timeSinceLastAttempt = Date.now() - lastSyncAttemptRef.current;
    
    if (syncMutation.isPending) return;
    if (timeSinceLastAttempt < MANUAL_COOLDOWN_MS) return;
    
    syncMutation.mutate();
  }, [syncMutation]);

  return {
    isConnected: !!connection?.is_active,
    isSyncing: syncMutation.isPending,
    lastSync: connection?.last_sync_at || null,
    triggerSync,
  };
}

export default useWhoopAutoSync;
